import { forwardRef, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'

import { FormError, Label } from 'shared/atoms'

import type { Ref } from 'react'
import type { Input as InputType } from './types'
import { cn } from 'utils/style'

export const InputGroup = forwardRef(
	(
		{
			id,
			label,
			name,
			icon,
			suffix,
			placeholder,
			onChange,
			disabled,
			readOnly,
			className,
			type = 'text',
			error,
			step,
			required = false,
			autoComplete
		}: InputType,
		ref: Ref<HTMLInputElement>
	) => {
		const [showPassword, setShowPassword] = useState(false)
		const toggleShowPassword = () => {
			setShowPassword((prev) => !prev)
		}
		const inputType = useMemo(
			() => (type === 'password' ? (showPassword ? 'text' : 'password') : type),
			[type, showPassword]
		)

		return (
			<div className={cn('flex flex-col gap-y-1.5', className)}>
				<Label htmlFor={id} required={required} text={label} />
				<div className="relative flex">
					{icon ? (
						<span className="grid shrink-0 place-items-center rounded-l-md border border-r-0 border-border bg-muted px-3 text-[13px] text-muted-foreground">
							{typeof icon === 'string' ? icon : <FontAwesomeIcon icon={icon} />}
						</span>
					) : null}
					<input
						type={inputType}
						id={id}
						name={name}
						placeholder={placeholder}
						onChange={onChange}
						ref={ref}
						disabled={disabled}
						readOnly={readOnly}
						step={step}
						autoComplete={autoComplete}
						className={cn(
							'h-10 w-full min-w-0 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none transition placeholder:text-muted-foreground/60 focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground',
							icon && 'rounded-l-none',
							suffix && 'rounded-r-none',
							type === 'password' && 'pr-10',
							readOnly && 'bg-muted text-muted-foreground focus:border-border focus:ring-0',
							error && 'border-danger focus:border-danger focus:ring-danger/20'
						)}
					/>
					{suffix ? (
						<span className="grid shrink-0 place-items-center rounded-r-md border border-l-0 border-border bg-muted px-3 text-[13px] text-muted-foreground">
							{suffix}
						</span>
					) : null}
					{type === 'password' ? (
						<button
							type="button"
							aria-label={showPassword ? 'Hide password' : 'Show password'}
							className="absolute inset-y-px right-px grid w-10 place-items-center rounded-r-[5px] border-l border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground [&>svg]:h-4 [&>svg]:w-4 [&>svg]:stroke-2"
							onClick={toggleShowPassword}>
							{showPassword ? <EyeSlashIcon /> : <EyeIcon />}
						</button>
					) : null}
				</div>
				<FormError text={error?.message} />
			</div>
		)
	}
)
